import { useNavigate } from "react-router-dom";
import { Clock, Sparkles, AlertTriangle, Crown } from "lucide-react";
import { Button } from "./ui/button";

export const SubscriptionBanner = ({ subscription }) => {
  const navigate = useNavigate();

  if (!subscription) return null;
  
  // Don't show banner for premium users
  if (subscription.is_premium) {
    return null;
  }

  const { account_type, free_sessions_remaining, trial_days_remaining, can_access_protocol } = subscription;

  // Expired trial
  if (account_type === "expired" || !can_access_protocol) {
    return (
      <div 
        className="glass rounded-xl p-4 border border-red-500/30 bg-red-500/5 mb-6"
        data-testid="subscription-banner-expired"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="font-medium text-white">Your free trial has ended</p>
              <p className="text-sm text-slate-400">
                Upgrade to continue your mental clarity journey
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/upgrade")}
            className="bg-brand-primary text-clarity-bg hover:bg-yellow-400 font-bold"
            data-testid="upgrade-now-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>
        </div>
      </div>
    );
  }

  // Low sessions warning (1-2 left)
  if (free_sessions_remaining <= 2 && free_sessions_remaining > 0) {
    return (
      <div 
        className="glass rounded-xl p-4 border border-amber-500/30 bg-amber-500/5 mb-6"
        data-testid="subscription-banner-warning"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-white">
                {free_sessions_remaining} free session{free_sessions_remaining !== 1 ? 's' : ''} remaining
              </p>
              <p className="text-sm text-slate-400">
                {trial_days_remaining > 0 
                  ? `${trial_days_remaining} days left in trial • Upgrade for unlimited access`
                  : "Upgrade to continue after your trial"
                }
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/upgrade")}
            className="bg-amber-500 text-clarity-bg hover:bg-amber-400 font-bold"
            data-testid="upgrade-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade
          </Button>
        </div>
      </div>
    );
  }

  // Normal trial status - Made more prominent
  if (account_type === "trial" && free_sessions_remaining > 2) {
    return (
      <div 
        className="rounded-xl p-4 mb-6 bg-gradient-to-r from-brand-primary/20 via-brand-primary/15 to-amber-500/20 border-2 border-brand-primary/50 shadow-[0_0_15px_rgba(229,184,76,0.15)]"
        data-testid="subscription-banner-trial"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-primary/30 flex items-center justify-center animate-pulse">
              <Sparkles className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <p className="font-semibold text-white text-base">
                <span className="text-brand-primary text-lg">{free_sessions_remaining}</span> free sessions remaining
              </p>
              {trial_days_remaining > 0 && (
                <p className="text-sm text-slate-300">
                  {trial_days_remaining} days left in your trial
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={() => navigate("/upgrade")}
            className="bg-brand-primary text-clarity-bg hover:bg-yellow-400 font-bold shadow-lg hover:shadow-[0_0_20px_rgba(229,184,76,0.4)] transition-all"
            data-testid="upgrade-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
